import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isFreeMessagingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  IconShare,
  IconCustomLocation,
  IconInstagram,
  IconWhatsapp,
  IconRate,
  AvatarLarge,
  Button,
  AvatarMedium,
  IconWeb,
  IconPhone,
  IconEmail,
  Modal,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import ShareButtons from '../../components/ShareButtons/ShareButtons';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';
import SectionFAQs from './SectionFAQs';

import verified from './../../assets/verified.png';
import recommended from './../../assets/recommended.png';

import css from './ListingPage.module.css';
import { paymentListingTypes, plainPlans } from '../../config/configStripe.js';
import { toSafeUri } from '../../util/string.js';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
  const [isShareSupported, setIsShareSupported] = useState(false);
  const [isUsingBrowser, setIsUsingBrowser] = useState(false);
  const [openVideo, setOpenVideo] = useState(false);

  useEffect(() => {
    setMounted(true);

    const isBrowser = typeof window !== 'undefined';
    setIsUsingBrowser(isBrowser);
    const hasWebShare = isBrowser && typeof window.navigator !== 'undefined' && typeof window.navigator.share === 'function';
    setIsShareSupported(Boolean(hasWebShare));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareDropdownOpen && !event.target.closest(`.${css.listingShare}`)) {
        setShareDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shareDropdownOpen]);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;
  let { firstName, lastName, } = currentUser?.attributes?.profile || {};
  const userName = [firstName, lastName].filter(s => !!s && s.trim() !== '').join(' ');

  const {
    listingType,
    transactionProcessAlias,
    unitType,
    is_recommended = false,
    is_verified = false,
    verifiedProfileRequested = false,
    location: listingLocation,
    instagramHandle = null,
    email = null,
    webUrl = null,
    phoneNumber = null,
    whatsappNumber = null,
    homeService = false,
    categoryLevel1,
    categoryArrayLevel2 = [],
    categoryArrayLevel3 = [],
    faqs: faqsRaw = [],
    video = null,
    accumulatedReviews = 0,
    totalReviews = 0,
    subscriptionKey = 'AcheiBase'
  } = publicData;

  let currentPlan = (plainPlans || []).find(p => p.id == subscriptionKey);

  let {
    allowLocation = false,
    allowPhotos = false,
    allowVideo = false,
    faqs: faqsLimit = 0,
    photosLimit = 0,
    social: allowSocial = false,
    whatsapp: allowWhatsapp = false,
  } = currentPlan || {};

  let faqs = faqsLimit > 0 ? faqsRaw.slice(0, faqsLimit) : [];

  const { address, building = '' } = listingLocation || {};
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig) && allowPhotos;

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isFree = isFreeMessagingProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const publicAuthorData = ensuredAuthor?.attributes?.profile?.publicData || {};
  let requestTitle = !!publicAuthorData.requestTitle;
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, ensuredAuthor, 'facebook');
  const twitterImages = listingImages(currentListing, ensuredAuthor, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    ensuredAuthor,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  let { categories = [] } = config?.categoryConfiguration || {};

  let selectedCategory = categories.find(c => c.id == categoryLevel1);

  const processShare = async () => {
    if (isShareSupported && typeof window !== 'undefined' && typeof window.navigator !== 'undefined' && typeof window.navigator.share === 'function') {
      const shareData = {
        title: title,
        text: intl.formatMessage({ id: 'ListingPage.shareText' }),
        url: window.location.href,
      };

      try {
        await window.navigator.share.call(window.navigator, shareData);
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      setShareDropdownOpen(!shareDropdownOpen);
    }
  };

  const showMissingPaymentFromListingType = paymentListingTypes.includes(listingType);
  const subject = encodeURIComponent(intl.formatMessage({ id: 'ListingPage.mailSubject' }));
  const body = encodeURIComponent(intl.formatMessage({ id: 'ListingPage.mailBody' }, { businessName: requestTitle ? title : authorDisplayName }));

  const ListingHeader = ({ showLocation = false, showRate = false, showShare = false, small = false }) => {
    const getAverageRating = () => {
      if (accumulatedReviews > 0 && totalReviews > 0) {
        return (accumulatedReviews / totalReviews).toFixed(1);
      }

      return 0;
    };

    const whatsappMessage = encodeURIComponent(intl.formatMessage({ id: 'ListingPage.whatsappMessage' }, { user: userName, service: title }));
    return (
      <div className={classNames(css.listingHeaderContainer, {[css.listingHeaderContainerSmall]: small})}>
        <div className={css.listingAvatar}>
          {small ?
            <AvatarMedium className={css.avatar} user={ensuredAuthor} />
            : <AvatarLarge className={css.avatar} user={ensuredAuthor} />}
          {!small ? <div className={classNames(css.listingPlanMobile)}>
            {verifiedProfileRequested && is_recommended ?
              <img src={recommended} alt="recommended"/>
              // : verifiedProfileRequested && is_verified ? <img src={verified} alt="verified"/>
              : null}
          </div> : null}
        </div>
        <div className={css.listingInfo}>
          <div className={css.listingTitleAndShare}>
            <div className={classNames(css.listingTitleAndDetails, {[css.listingTitleAndDetailsSmall]: small})}>
              <div className={css.listingTitleAndPrice}>
                <div>{title}</div>
                {!isFree && showMissingPaymentFromListingType ? <div>{intl.formatMessage({ id: `ListingPage.headerPrice${unitType}` })} <span>{formattedPrice}</span></div> : null}
              </div>
              {showRate ? <div className={css.listingRating}>
                <IconRate/>
                <span className={css.reviewsAvg}>{getAverageRating()}</span>
                <span>({totalReviews})</span>
              </div> : null}
              <div className={classNames(small ? css.listingPlanSmall : css.listingPlan)}>
                {verifiedProfileRequested && is_recommended ?
                  <img src={recommended} alt="recommended"/>
                  // : verifiedProfileRequested && is_verified ? <img src={verified} alt="verified"/>
                  : null}
              </div>
            </div>
            {showShare && isUsingBrowser ? <div className={css.listingShare}>
              <button
                className={css.shareButton}
                onClick={processShare}
                type="button"
              >
                <IconShare/>
              </button>
              {shareDropdownOpen ? (
                <div className={css.shareDropdown}>
                  <ShareButtons
                    url={window.location.href}
                    title={title}
                    intl={intl}
                    description={description}
                    isDropdown={true}
                  />
                </div>
              ) : null}
            </div> : null}
          </div>
          {address && showLocation && allowLocation ? <div className={css.listingAddress}>
            <IconCustomLocation/>
            <span>{[address, building].filter(a => !!a && a.trim() !== '').join(', ')}</span>
          </div> : null}
          <div className={css.listingSocial}>
            {phoneNumber && <a target="_blank" href={`tel:${phoneNumber.replace(/\D/g, '')}`}>
              <IconPhone/>
            </a>}
            {email && email.trim() !== '' && <a target="_blank" href={`mailto:${email}?subject=${subject}&body=${body}`}>
              <IconEmail/>
            </a>}
            {whatsappNumber && allowWhatsapp && <a target="_blank" href={`https://wa.me/${whatsappNumber.replace(/\D/g, '')}?text=${whatsappMessage}`}>
              <IconWhatsapp/>
            </a>}
            {instagramHandle && allowSocial && <a target="_blank" href={`https://www.instagram.com/${instagramHandle}`}>
              <IconInstagram/>
            </a>}
            {webUrl && allowSocial && <a target="_blank" href={toSafeUri(webUrl)}>
              <IconWeb/>
            </a>}
          </div>
        </div>
      </div>
    );
  };

  const hasImages = currentListing?.images?.length > 0;

  const goBack = () => {
    history.goBack();
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.voltarBtn} onClick={goBack}>{intl.formatMessage({ id: 'ListingPage.voltar' })}</div>
          <div className={css.mainColumnForProductLayout}>
            {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing && showMissingPaymentFromListingType ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                currentUser={currentUser}
              />
            ) : null}
            {mounted && currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                currentUser={currentUser}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}
            <ListingHeader
              showLocation
              showRate
              showShare
            />
            <div className={css.propsContainer}>
              <div className={css.listingProp}>
                <div>{intl.formatMessage({ id: 'ListingPage.descriptionLabel' })}</div>
                <div className={css.listingText}>{description}</div>
              </div>
              {homeService && <div className={css.singleProp}>{intl.formatMessage({ id: 'ListingPage.homeService' })}</div>}
              {!isFree && !isOwnListing && showMissingPaymentFromListingType ? <Button className={css.contactBtn} type="button" onClick={() => onContactUser(currentUser)}>
                {intl.formatMessage({ id: 'ListingPage.sendMessage' })}
              </Button> : null}
            </div>
            {selectedCategory ? <div className={css.propsContainer}>
              <div className={css.listingProp}>
                <div>{intl.formatMessage({ id: 'ListingPage.servicesLabel' })}</div>
                <div className={css.mainCategory}>{selectedCategory.name}</div>
                {selectedCategory.subcategories.filter(sc => categoryArrayLevel2.includes(sc.id)).map(sc => {
                  return <div className={css.secondCategory} key={sc.id}>
                    <div>{sc.name}</div>
                    <div>
                      {sc.subcategories.filter(tc => categoryArrayLevel3.includes(tc.id)).map(tc => {
                        return <div className={css.thirdCategory} key={tc.id}>
                          {tc.name}
                        </div>
                      })}
                    </div>
                  </div>;
                })}
              </div>
            </div> : null}
            {showListingImage && (hasImages || video) && (<div className={css.propsContainer}>
              <div className={css.listingProp}>
                <div>{intl.formatMessage({ id: 'ListingPage.media' })}</div>
                {hasImages ? <SectionGallery
                  listing={currentListing}
                  photosLimit={photosLimit}
                  variantPrefix={config.layout.listingImage.variantPrefix}
                  onManageDisableScrolling={onManageDisableScrolling }
                /> : null}
                {video && allowVideo ? <><div className={css.videoContainer} onClick={() => setOpenVideo(true)}>
                    <video className={css.videoThumbnail} src={video}/>
                  </div>
                  <Modal
                    id="ListingPage.videoModal"
                    isOpen={openVideo}
                    onClose={() => setOpenVideo(false)}
                    onManageDisableScrolling={onManageDisableScrolling}
                    usePortal
                  >
                    <video className={css.videoModal} src={video} controls/>
                  </Modal></>
                : null}
              </div>
            </div>)}
            {/* <div
              className={showListingImage ? css.mobileHeading : css.noListingImageHeadingProduct}
            >
              {showListingImage ? (
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              ) : (
                <H3 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H3>
              )}
            </div> */}

            {/* <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            /> */}

            {/* <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            /> */}
            <div className={classNames(css.propsContainer, css.noBorderBottom)}>
              <div className={css.listingProp}>
                <div>{intl.formatMessage({ id: 'ListingPage.reviews' })}</div>
                <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
              </div>
            </div>

            {faqsLimit > 0 && faqs.length > 0 ? <SectionFAQs faqs={faqs} intl={intl} /> : null}

            {!isFree && !isOwnListing && showMissingPaymentFromListingType ? <div className={classNames(css.propsContainer, css.noBorderBottom)}>
              <Button className={css.contactBtn} type="button" onClick={() => onContactUser(currentUser)}>
                {intl.formatMessage({ id: 'ListingPage.sendMessage' })}
              </Button>
            </div> : null}
            <SectionAuthorMaybe
              title={title}
              visible={false}
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </div>
          <div className={css.orderColumnForProductLayout}>
            <ListingHeader small/>
            <OrderPanel
              className={classNames(css.productOrderPanel, {
                [css.imagesEnabled]: showListingImage,
              })}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              hideTitle
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              // title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                isBooking ? <div className={css.selectDate}>{intl.formatMessage({ id: 'ListingPage.selectDate' })}</div> : null
              }
              payoutDetailsWarning={showMissingPaymentFromListingType ? payoutDetailsWarning : null}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              showListingImage={showListingImage}
            />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
